import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BreadCrumb } from 'src/app/models/common/bread-crumb';
import { PageHeader } from 'src/app/models/common/page-header';
import { BaseSearchComponent } from 'src/app/system-common/base-search/base-search.component';
import { HeaderService } from 'src/app/system-common/services/header.service';
import { SettingService } from 'src/app/system-common/services/setting.service';
import { PolicyDetailsService } from '../services/policydetails.service';
import * as Chart from 'chart.js';
import * as ChartDataLabels from 'chartjs-plugin-datalabels';

// Chart.plugins.register(ChartDataLabels);

@Component({
  selector: '',
  template: '',
})
export class MyPoliciesSearchHandler extends BaseSearchComponent<PolicyDetailsService> implements OnInit {
  chart: Chart;
  searchTerm = '';
  policies;
  productChart: Chart;
  statusChart: Chart;
  // product chart variables
  productChartLabels = [];
  productChartData = [];

  productChartColors = [
    '#FF6384',
    '#FF9F40',
    '#FFCD56',
    '#4BC0C0',
    '#36A2EB',
    '#9966FF',
    '#C9CBCF',
    '#84FF63',
    '#63FFDA',
    '#FF6389',
  ];

  // status chart variables
  statusChartLabels = [];
  statusChartData = [];
  statusChartColors = ['#FF9F40', '#FF6384', '#28a745'];
  public currentSelectedTheme;

  constructor(
    public service: PolicyDetailsService,
    public headerService: HeaderService,
    public router: Router,
    public settingService: SettingService
  ) {
    super(service, router, headerService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.currentSelectedTheme = this.settingService.getSessionStorageValue('currentSelectedTheme');
    if (
      (this.currentSelectedTheme != undefined || this.currentSelectedTheme != null) &&
      this.currentSelectedTheme.endsWith('.css')
    ) {
      this.currentSelectedTheme = this.currentSelectedTheme.replace('.css', '');
    }
  }

  protected getBreadCrumbList(): BreadCrumb[] {
    return [
      {
        id: 'Home',
        link: '/home',
      },
      {
        id: 'Search',
      },
    ];
  }

  protected getPageHeader(): PageHeader {
    let header = new PageHeader();
    header.title = this.settingService.getLabelValue('myPolicies');
    return header;
  }

  loadAllPolicies() {
    this.service.getMyPolicies().subscribe((res: any) => {
      if (res?.data?.policies) {
        this.policies = res.data.policies;
      }
    });
  }

  loadProductChartdata() {
    this.service.getMyPolicies('baseplancode').subscribe((res: any) => {
      if (res?.data?.groupedPolicies) {
        const grouped = res.data.groupedPolicies;

        this.productChartLabels = grouped.map((p) => p.baseplancode);
        this.productChartData = grouped.map((p) => p.count);

        this.renderProductChart();
      }
    });
  }

  loadStatusChartdata() {
    this.service.getMyPolicies('statuscode').subscribe((res: any) => {
      if (res?.data?.groupedPolicies) {
        const grouped = res.data.groupedPolicies;
        this.statusChartLabels = grouped.map((p) => p.statuscode);
        this.statusChartData = grouped.map((p) => p.count);
        this.renderStatusChart();
      }
    });
  }

  // filteredPolicies() {
  //   return this.policies.filter((p) => p.toLowerCase().includes(this.searchTerm.toLowerCase()));
  // }

  filteredPolicies() {
    const term = (this.searchTerm ?? '').toLowerCase();
    if (!this.policies) return [];
    return this.policies.filter((p) => p != null && p.toString().toLowerCase().includes(term));
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.loadAllPolicies();
      this.loadProductChartdata();
      this.loadStatusChartdata();
    }, 10);
  }

  renderProductChart() {
    if (this.productChart) this.productChart.destroy();

    this.productChart = new Chart('productPieCanvas', {
      type: 'pie',
      data: {
        labels: this.productChartLabels,
        datasets: [
          {
            data: this.productChartData,
            backgroundColor: this.productChartColors,
            hoverBackgroundColor: this.productChartColors,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          datalabels: {
            formatter: (value, context) => {
              const data = context.chart.data.datasets[0].data;
              const total = data.reduce((a: number, b: number) => a + b, 0);
              const percentage = (value / total) * 100;
              return percentage.toFixed(2) + '%';
            },
            color: '#000',
            font: {
              weight: 'bold',
              size: 10,
              family: 'montserratregular, Arial, Helvetica, sans-serif',
            },
            anchor: 'center',
            align: 'center',
            clip: true,
            display: function (context) {
              const data = context.chart.data.datasets[0].data;
              const total = data.reduce((a: number, b: number) => a + b, 0);
              const value = data[context.dataIndex];
              const percentage = (value / total) * 100;
              return percentage >= 10;
            },
          },
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItems, data) {
              let total = data.datasets[0].data.reduce((a, b) => a + b, 0);
              const value = data.datasets[tooltipItems.datasetIndex].data[tooltipItems.index];
              const percentage = (value / total) * 100;
              return data.labels[tooltipItems.index] + ' : ' + percentage.toFixed(2) + '%';
            },
          },
          bodyFontFamily: 'montserratregular, Arial, Helvetica, sans-serif',
          bodyFontSize: this.settingService.isMobileViewRendered() ? 10 : 12,
        },
        legend: {
          display: true,
          onClick: () => {},
          position: 'right',
          labels: {
            fontSize: this.settingService.isMobileViewRendered() ? 8 : 12,
            fontColor: this.currentSelectedTheme == 'dark' ? '#fff' : '#000',
            fontFamily: 'montserratregular, Arial, Helvetica, sans-serif',
            boxWidth: this.settingService.isMobileViewRendered() ? 15 : 40,
            padding: this.settingService.isMobileViewRendered() ? 3 : 10,
          },
        },
      },
      plugins: [ChartDataLabels],
    });
  }

  renderStatusChart() {
    if (this.statusChart) this.statusChart.destroy();

    this.statusChart = new Chart('statusPieCanvas', {
      type: 'pie',
      data: {
        labels: this.statusChartLabels,
        datasets: [
          {
            data: this.statusChartData,
            backgroundColor: this.statusChartColors,
            hoverBackgroundColor: this.statusChartColors,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          datalabels: {
            formatter: (value, context) => {
              const data = context.chart.data.datasets[0].data;
              const total = data.reduce((a: number, b: number) => a + b, 0);
              const percentage = (value / total) * 100;
              return percentage.toFixed(2) + '%';
            },
            color: '#000',
            font: {
              weight: 'bold',
              size: 10,
              family: 'montserratregular, Arial, Helvetica, sans-serif',
            },
            anchor: 'center',
            align: 'center',
            clip: true,
            display: function (context) {
              const data = context.chart.data.datasets[0].data;
              const total = data.reduce((a: number, b: number) => a + b, 0);
              const value = data[context.dataIndex];
              const percentage = (value / total) * 100;
              return percentage >= 10;
            },
          },
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItems, data) {
              let total: number = 0;
              for (let i = 0; i < data.datasets[0].data.length; i++) {
                total = total + data.datasets[0].data[i];
              }
              const percentage = (data.datasets[tooltipItems.datasetIndex].data[tooltipItems.index] / total) * 100;
              const formattedPercentage = percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(2);

              return data.labels[tooltipItems.index] + ' : ' + formattedPercentage + '%';
            },
          },
          bodyFontFamily: 'montserratregular, Arial, Helvetica, sans-serif',
          bodyFontSize: this.settingService.isMobileViewRendered() ? 10 : 12,
        },
        legend: {
          display: true,
          onClick: () => {},
          position: 'right',
          labels: {
            fontSize: this.settingService.isMobileViewRendered() ? 8 : 12,
            fontColor: this.currentSelectedTheme == 'dark' ? '#fff' : '#000',
            fontFamily: 'montserratregular, Arial, Helvetica, sans-serif',
            boxWidth: this.settingService.isMobileViewRendered() ? 15 : 40,
            padding: this.settingService.isMobileViewRendered() ? 3 : 10,
          },
        },
      },
      plugins: [ChartDataLabels],
    });
  }
}
